import {
  HOURS_PER_DAY,
  MINUTES_PER_HOUR,
  MONTHS_PER_YEAR
} from "../../lib/constants";
import type { EcommerceInputs } from "./defaults";

const ERROR_REDUCTION_FACTOR = 0.5;
const ECOMMERCE_CSR_PORTION = 0.4;

export interface EcommerceResult {
  annualOnlineRevenue: number;
  annualOnlineOrders: number;
  monthlyOnlineOrders: number;
  monthlyErrorSavings: number;
  monthlyCsrSavings: number;
  monthlyCost: number;
  firstYearMonthlySavings: number;
  totalMinutesSavedPerMonth: number;
  totalDaysSavedPerMonth: number;
}

function safeNonNegative(value: number): number {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }

  return value;
}

function safeSigned(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return value;
}

export function calculateEcommerceRoi(inputs: EcommerceInputs): EcommerceResult {
  const onlineSalesRatio = inputs.onlineSalesPercent / 100;
  const errorRateRatio = inputs.currentErrorRatePercent / 100;

  const annualOnlineRevenue = safeNonNegative(inputs.annualDirectSalesRevenue * onlineSalesRatio);
  const annualOnlineOrders = safeNonNegative(
    inputs.averageOrderValue > 0 ? annualOnlineRevenue / inputs.averageOrderValue : 0
  );
  const monthlyOnlineOrders = safeNonNegative(annualOnlineOrders / MONTHS_PER_YEAR);

  const monthlyErrorSavings = safeNonNegative(
    monthlyOnlineOrders * errorRateRatio * ERROR_REDUCTION_FACTOR * inputs.averageErrorCost
  );

  const monthlyCsrCost = safeNonNegative(
    (inputs.csrHeadCount * inputs.averageCsrSalary) / MONTHS_PER_YEAR
  );
  const monthlyCsrSavings = safeNonNegative(monthlyCsrCost * ECOMMERCE_CSR_PORTION);
  const monthlyCost = safeNonNegative(
    inputs.monthlySoftwareAmount + inputs.implementationFee / MONTHS_PER_YEAR
  );

  const firstYearMonthlySavings = safeSigned(monthlyErrorSavings + monthlyCsrSavings - monthlyCost);

  const totalMinutesSavedPerMonth = safeNonNegative(monthlyOnlineOrders * inputs.averageMinutesPerOrder);
  const totalDaysSavedPerMonth = safeNonNegative(
    totalMinutesSavedPerMonth / MINUTES_PER_HOUR / HOURS_PER_DAY
  );

  return {
    annualOnlineRevenue,
    annualOnlineOrders,
    monthlyOnlineOrders,
    monthlyErrorSavings,
    monthlyCsrSavings,
    monthlyCost,
    firstYearMonthlySavings,
    totalMinutesSavedPerMonth,
    totalDaysSavedPerMonth
  };
}

export const ECOMMERCE_ASSUMPTIONS = {
  errorReductionFactor: ERROR_REDUCTION_FACTOR,
  csrPortion: ECOMMERCE_CSR_PORTION
};
